/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import classNames from 'classnames';
import React from 'react';
import { theme } from 'styles/theme/theme';
import UDText from '../text';
import { MODAL_BODY_CLASS } from './modal-layer-context';

// Modal Header
export interface ModalHeaderProps {
  title: string;
  subtitle?: string;
  className?: string;
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({ title, subtitle, className }) => {
  return (
    <div className={classNames('modal-header mb-2.5', className)}>
      <UDText as="h2" variant="title-3" className="center">
        {title}
      </UDText>
      {subtitle && (
        <UDText variant="text-2" className="center text-inkMuted mt-2.5 whitespace-pre-wrap">
          {subtitle}
        </UDText>
      )}
    </div>
  );
};

// Modal Body
export interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalBody: React.FC<ModalBodyProps> = ({ children, className }) => {
  return (
    <div className={classNames(MODAL_BODY_CLASS, className)} css={modalBodyStyles}>
      {children}
    </div>
  );
};

/**
 * Тело окна прокручивается само, шапка и кнопки стоят (`ModalContainer` в `styles.ts`).
 * `Modal.Footer` внутри тела — форма, где кнопки лежат в `<form>` вместе с полями, — прилипает
 * к низу тела, поэтому тоже не уезжает. Без `z-index`: позиционированный футер и так поверх
 * полей, а раскрытый список (`z-index: 1`) должен быть поверх футера.
 */
const modalBodyStyles = () => css`
  flex: 1;
  min-height: 0;
  overflow: auto;

  .modal-footer {
    position: sticky;
    bottom: 0;
    background: ${theme.colors.SurfaceCard};
  }
`;

// Modal Footer
export interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
  justify?: 'start' | 'center' | 'end' | 'between';
}

export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className, justify = 'end' }) => {
  const justifyClass = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
  }[justify];

  return (
    <div className={classNames('modal-footer flex items-center gap-3', justifyClass, className)} css={modalFooterStyles}>
      {children}
    </div>
  );
};

const modalFooterStyles = css`
  margin-top: 40px;
  width: 100%;
`;
